'use client'

import React, { useEffect, useState } from 'react'
import { gql, useLazyQuery } from '@apollo/client'
import ProductCard from './ProductCard'
import { AuthRequired } from '@/components/authRequired'
import { isIngredientInSeason } from '@/lib/seasonal-utils'

// The public products query: verified, non-archived products only, and open to logged-out visitors. The
// owner's getGetDirectoryIngredientByListingID is login-only and also returns private/pending products,
// so it must not be used here.
const PUBLIC_LISTING_PRODUCTS = gql`
  query PublicListingProducts($listingID: Int!) {
    getPublicListingProducts(listingID: $listingID) {
      ingredient_id
      illustrationImage
      mainImage
      name
      slug
      description
      isSeasonal
      start_month
      end_month
      availableMonths {
        month
        isPeak
      }
    }
  }
`

const PREVIEW_DESKTOP = 4
const PREVIEW_MOBILE = 2
const GATE_THRESHOLD = 4

interface ProductPreviewProps {
  listingId: number
  listingName?: string
  listingType?: 'producer' | 'market'
  followerCount?: number
  // Show every product with no join card underneath. The page prompts to join on any click instead.
  showAll?: boolean
  // The page's own "no products" design (the same empty state the profile page shows to visitors).
  renderEmpty?: () => React.ReactNode
}

const ProductPreview: React.FC<ProductPreviewProps> = ({ listingId, listingName, listingType = 'producer', followerCount, showAll = false, renderEmpty }) => {
  const [products, setProducts] = useState<any[]>([])
  const [loaded, setLoaded] = useState(false)
  const [getProducts] = useLazyQuery(PUBLIC_LISTING_PRODUCTS, { fetchPolicy: 'no-cache' })

  useEffect(() => {
    if (!listingId) return
    getProducts({ variables: { listingID: listingId } }).then((res) => {
      setProducts(res.data?.getPublicListingProducts || [])
      setLoaded(true)
    })
  }, [listingId])

  const total = products.length
  const sorted = [...products].sort((a, b) => Number(isIngredientInSeason(b || {})) - Number(isIngredientInSeason(a || {})))
  const preview = showAll ? sorted : total > GATE_THRESHOLD ? sorted.slice(0, PREVIEW_DESKTOP) : []

  return (
    <>
      {preview.length > 0 && (
        <div className="mt-4">
          {/* desktop: 4 items; mobile: first row = 2 */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
            {preview.map((product: any, index: number) => (
              <div key={product.ingredient_id ?? index} className={!showAll && index >= PREVIEW_MOBILE ? 'hidden sm:block' : ''}>
                <ProductCard
                  id={product.ingredient_id}
                  slug={product.slug}
                  name={product.name}
                  imageUrl={product.mainImage || product.illustrationImage || '/assets/icons/Broccoli.svg'}
                  listings={product.listings}
                  description={product.description}
                  availableNow={isIngredientInSeason(product || {})}
                  isDeletable={false}
                  deleteProduct={() => {}}
                />
              </div>
            ))}
          </div>
          {total > preview.length && (
            <p className="mt-3 text-center text-sm text-gray-400">
              +{total - preview.length} more products
            </p>
          )}
        </div>
      )}
      {showAll && loaded && total === 0 ? (
        renderEmpty ? renderEmpty() : <p className="mt-6 text-center text-sm text-gray-500">No products listed yet.</p>
      ) : null}
      {showAll ? null : (
        <AuthRequired
          listingName={listingName}
          listingType={listingType}
          tab="products"
          followerCount={followerCount}
          totalCount={total > GATE_THRESHOLD ? total : undefined}
        />
      )}
    </>
  )
}

export default ProductPreview
